export interface EditorPermission {
  id: string
  label: string
  routes: string[]
  legacy?: boolean
}

export interface EditorPermissionGroup {
  label: string
  permissions: EditorPermission[]
}

export const EDITOR_PERMISSION_GROUPS: EditorPermissionGroup[] = [
  {
    label: 'Notifications and content',
    permissions: [
      { id: 'mobile_notifications', label: 'Mobile Notifications', routes: ['mobile-notifications'], legacy: true },
      { id: 'banner', label: 'Banner', routes: ['bannerPage'] },
      { id: 'popup_ads', label: 'Popup Ad', routes: ['popup-ads'], legacy: true },
      { id: 'large_item_types', label: 'Large Item Types', routes: ['largeItemTypePage'] }
    ]
  },
  {
    label: 'Products and catalog',
    permissions: [
      { id: 'category', label: 'Category', routes: ['category'] },
      { id: 'subcategory', label: 'Subcategory', routes: ['subcategory'] },
      { id: 'product_lists', label: 'Product Lists', routes: ['product', 'edit-product'], legacy: true },
      { id: 'product_audits', label: 'Product Audits', routes: ['product-audits'], legacy: true },
      { id: 'variation_lists', label: 'Variation Lists', routes: ['item-lists'], legacy: true },
      { id: 'add_product', label: 'Add Product', routes: ['add-product'], legacy: true },
      { id: 'bulk_upload_products', label: 'Bulk Upload Products', routes: ['bulk-upload-products'], legacy: true },
      { id: 'bulk_stock_price_update', label: 'Bulk Stock/Price Update', routes: ['bulk-stock-price-update-products'], legacy: true },
      { id: 'deleted_products', label: 'Deleted Product', routes: ['deletedProduct'], legacy: true },
      { id: 'brand', label: 'Brand', routes: ['brandPage'] },
      { id: 'seller_brand', label: 'Seller Brand', routes: ['sellerBrand'], legacy: true }
    ]
  },
  {
    label: 'Sellers and customers',
    permissions: [
      { id: 'seller_lists', label: 'Seller Lists', routes: ['seller', 'sellerDetail'], legacy: true },
      { id: 'pending_sellers', label: 'Pending Sellers', routes: ['pending-sellers'], legacy: true },
      { id: 'rejected_sellers', label: 'Rejected Sellers', routes: ['rejected-sellers'], legacy: true },
      { id: 'seller_reviews', label: 'Seller Reviews', routes: ['review-sellers'], legacy: true },
      { id: 'deleted_sellers', label: 'Deleted Sellers', routes: ['deletedSeller'], legacy: true },
      { id: 'customer_list', label: 'Customer List', routes: ['customer', 'customer-order-lists'], legacy: true },
      { id: 'return_requests', label: 'Return Requests', routes: ['customer-return-requests'], legacy: true }
    ]
  },
  {
    label: 'Orders',
    permissions: [
      { id: 'order_list', label: 'Order List', routes: ['orderList', 'orderDetail', 'orderDetailAdmin'], legacy: true },
      { id: 'manage_region', label: 'Manage Region', routes: ['manageRegion'], legacy: true }
    ]
  },
  {
    label: 'Communication',
    permissions: [
      { id: 'customer_chat', label: 'Customer Chat', routes: ['seller_chat', 'seller_chat_conversation'], legacy: true }
    ]
  },
  {
    label: 'Reviews and support',
    permissions: [
      { id: 'question_answer', label: 'Question & Answer', routes: ['questionAdmin', 'answerAdmin'], legacy: true },
      { id: 'reviews', label: 'Review', routes: ['reviewAdmin', 'reviewDetailAdmin'], legacy: true },
      { id: 'admin_reviews', label: 'Admin Reviews', routes: ['admin-review-products'] }
    ]
  },
  {
    label: 'Marketing',
    permissions: [
      { id: 'promotion_events', label: 'Promotion Events', routes: ['admin-promotion-events', 'admin-join-promotion-events', 'promotion-event-detail'], legacy: true },
      { id: 'promotion_codes', label: 'Promotion Codes', routes: ['admin-promotion-codes'], legacy: true },
      { id: 'referral_codes', label: 'Referral Codes', routes: ['admin-referrals'], legacy: true }
    ]
  },
  {
    label: 'Finance',
    permissions: [
      { id: 'customer_finance', label: 'Customer Finance', routes: ['customer-finance'], legacy: true },
      { id: 'seller_finance', label: 'Seller Finance', routes: ['seller-finance'], legacy: true },
      { id: 'monthly_reports', label: 'Monthly Reports', routes: ['monthly-reports'] }
    ]
  },
  {
    label: 'Administration',
    permissions: [
      { id: 'services_category', label: 'Services Category', routes: ['marketPlacePage'] },
      { id: 'service_provider', label: 'Service Provider', routes: ['serviceAreaPage'] },
      { id: 'route_stats', label: 'Route Stats', routes: ['admin-route-stats'] },
      { id: 'feature_settings', label: 'Feature Settings', routes: ['feature-settings'] },
      { id: 'delivery_accounts', label: 'Delivery Accounts', routes: ['deliveryPage', 'deliverySearchPage', 'deliveryDetailPage'] },
      { id: 'google_analytics', label: 'Google Analytics', routes: [] }
    ]
  }
]

export const EDITOR_PERMISSIONS: EditorPermission[] = EDITOR_PERMISSION_GROUPS.flatMap(group => group.permissions)

// Editors created before configurable permissions keep the tabs they already had until an admin saves their access.
export const LEGACY_EDITOR_PERMISSIONS = EDITOR_PERMISSIONS
  .filter(permission => permission.legacy)
  .map(permission => permission.id)

export function hasEditorPermission(permissionId: string, permissions: unknown): boolean {
  const effectivePermissions = permissions == null
    ? LEGACY_EDITOR_PERMISSIONS
    : Array.isArray(permissions)
      ? permissions
      : []

  return effectivePermissions.includes(permissionId)
}

export function getEditorPermissionForRoute(routeName: string): string | null {
  const permission = EDITOR_PERMISSIONS.find(item => item.routes.includes(routeName))
  return permission ? permission.id : null
}
